<template>
  <div>
    <div class="overview" :class="location.darkMode ? 'dark' : ''">
      <OverviewHeader :momentLastConnected="momentLastConnected"
      :isNetworkAvailable="isNetworkAvailable"
      :location="location"
      :calendarDate="calendarDate"
      :darkMode="darkMode"
      @toggleDarkMode="toggleDarkMode"
      @setCalendarDate="setCalendarDate"
      @setOverviewScrollPosition="setOverviewScrollPosition"
      @now="now" />
      <div class="stationsandcalendar">
        <StationList :isNetworkAvailable="isNetworkAvailable"
        :experiences="experiences"
        :stations="stations"
        :darkMode="darkMode"
        @stationClearCalling="stationClearCalling"
        @restoreStation="restoreStation" />
        <Calendar class="only-tablet"
        :calendarHeaderTimes="calendarHeaderTimes"
        :showNowLine="showNowLine"
        :experiences="experiences"
        :stations="stations"
        :bookings="bookings"
        :closings="closings"
        :calendarDate="calendarDate"
        :twentyFourHourTime="location.twentyFourHourTime"
        :isNetworkAvailable="isNetworkAvailable"
        :isOrganizationEducation="isOrganizationEducation"
        :darkMode="darkMode"/>
      </div>

      <OverviewModals />

      <SafetyMode :isNetworkAvailable="isNetworkAvailable"
      @enterSafetyMode="now" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import moment from 'moment-timezone';

import StationList from './station-list/StationList';
import Calendar from './calendar/Calendar';
import OverviewHeader from './OverviewHeader';
import OverviewModals from './OverviewModals';
import OverviewHelper from '@/location/overview/OverviewHelper';
import DateHelper from '@/core/shared/helpers/DateHelper';
import SafetyMode from './safety-mode/SafetyMode';

@Component({
  props: {
    locationId: {
      type: String,
      required: true,
    },
  },
  components: {
    StationList,
    Calendar,
    OverviewHeader,
    OverviewModals,
    SafetyMode,
  },
})
export default class OverviewRoute extends Vue {
  calendarHeaderTimes = [];
  darkMode = this.location.darkMode;

  get calendarDate () { return this.$store.getters.calendarDate; }
  get showNowLine () { return moment(this.$store.state.todaysDate).isSame(moment(this.calendarDate), 'day'); }
  get isNetworkAvailable () { return this.$store.getters.isNetworkAvailable; }
  get isOrganizationEducation () { return this.$permissions.education; }
  get momentLastConnected () { return this.$store.getters.momentLastConnected; }
  get overviewScrollPosition () { return this.$store.getters.overviewScrollPosition; }

  get location () {
    return this.$store.getters['entities/Location'](this.locationId);
  }

  get stations () {
    return this.$store.getters.locationStations(this.locationId);
  }

  get experiences () {
    return this.$store.getters.locationExperiences(this.locationId);
  }

  get bookings () {
    return this.$store.getters.locationBookingsOnDate({
      locationId: this.locationId,
      date: moment(this.calendarDate).set({
        hours: 12,
        minutes: 0,
        seconds: 0,
      }).format(),
    });
  }

  get closings () {
    return this.$store.getters.locationClosingsOnDate({
      locationId: this.locationId,
      date: moment(this.calendarDate).set({
        hours: 12,
        minutes: 0,
        seconds: 0,
      }).format(),
    });
  }

  created () {
    const currentTime = this.$store.getters.currentTime;

    if (this.overviewScrollPosition) {
      OverviewHelper.scrollToPx(this.overviewScrollPosition.left, this.overviewScrollPosition.top);
    } else if (DateHelper.dateMatch(currentTime, this.calendarDate)) {
      setTimeout(() => { OverviewHelper.scrollToTime(currentTime); });
    }
    this.calendarHeaderTimes = OverviewHelper.getTimesForCalendarHeader(this.location.twentyFourHourTime);
  }

  setCalendarDate (date) {
    this.$store.commit('setCalendarDate', date);
  }

  stationClearCalling (stationId) {
    this.$store.dispatch('storeStation', { id: stationId, calling: false });
  }

  restoreStation (stationId) {
    const article = `<a slot="article" href="https://svr.li/k/234" rel=noopener target=_blank>${this.$t('Safety Mode article')}</a>`;
    this.$store.commit('setConfirm', {
      title: this.$t('Are you sure you want to restore this station?'),
      icon: 'icon-rotate-cw',
      message: `
        <div class="left-align">
          <p>${this.$t('If a customer is currently playing on this station, make sure there is a running session for it in the Station Monitor or the player will have their session ended unintentionally. If you do not have a running session in the Station Monitor:')}</p>
          <ul>
            <li>${this.$t('Create a new walk-in, adjust the time, check in and start.')}</li>
            <li>${this.$t('Then restore your station.')}</li>
          </ul>
          <p>${this.$t('Read this {article} to learn more.', { article })}</p>
        </div>
      `,
      buttons: [
        {
          name: this.$t('Cancel'),
          type: 'cancel',
        },
        {
          name: this.$t('Restore Station'),
          action: () => {
            const station = this.$store.getters['entities/Station'](stationId);
            this.$store.dispatch('restoreStation', { id: station.id });
          },
        },
      ],
    });
  }

  setOverviewScrollPosition () {
    this.$store.commit('setOverviewScrollPosition');
  }

  now () {
    const currentTime = this.$store.getters.currentTime;
    this.setCalendarDate(currentTime);
    OverviewHelper.scrollToTime(currentTime);
  }

  toggleDarkMode () {
    this.$store.dispatch('storeLocation', this.location);
  }
}
</script>

<style scoped lang="postcss">
@import 'core/shared/styles';

.overview {
  position: relative;
  width: 100%;
  border-top: solid 3px var(--colorSBRedFlat);
  margin-top: 5px;
  & .stationsandcalendar {
    display: flex;
    z-index: var(--zContent);

    & .station-list {
      z-index: 2;
      flex: 1 1 auto;

      @media (--tablet) {
        flex: 0 0 37rem;
      }
    }

    & .calendar {
      display: none;

      @media (--tablet) {
        display: block;
      }
    }
  }
}

.overview.dark {
  background-color: var(--darkModeColorDarkBackground);
}

</style>
